import type { AttentionItem, AttentionReadModel, AttentionSourceStatus } from './api';

export type AttentionSlice = {
  requires_you: AttentionItem[];
  in_progress: AttentionItem[];
  waiting: AttentionItem[];
  sources: Partial<AttentionReadModel['sources']>;
};

const MONEY_RENT_STATUSES = new Set(['Нужно сверить', 'Просрочен платёж', 'Платёж сегодня']);

function mergeLane(base: AttentionItem[], extra: AttentionItem[]): AttentionItem[] {
  const seen = new Set(base.map((item) => item.key));
  const next = base.slice();
  for (const item of extra) {
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    next.push(item);
  }
  return next;
}

function coveredByMoney(item: AttentionItem, units: Set<string>): boolean {
  if (item.source_state !== 'rent_payment' || !item.unit_code) return false;
  if (item.source !== 'oleg' && item.source !== 'task' && item.source !== 'task_inbox') return false;
  return units.has(item.unit_code.toUpperCase());
}

/** Drop a rent-payment card once the money forecast already shows that unit. */
export function mergeAttentionModel(base: AttentionReadModel, slice: AttentionSlice): AttentionReadModel {
  const merged: AttentionReadModel = {
    ...base,
    requires_you: mergeLane(base.requires_you, slice.requires_you),
    in_progress: mergeLane(base.in_progress, slice.in_progress),
    waiting: mergeLane(base.waiting, slice.waiting),
    sources: { ...base.sources, ...slice.sources },
  };
  const units = new Set(
    [...merged.requires_you, ...merged.in_progress, ...merged.waiting]
      .filter((item) => item.source === 'money' && item.unit_code && MONEY_RENT_STATUSES.has(item.status_label))
      .map((item) => item.unit_code!.toUpperCase()),
  );
  if (!units.size) return merged;
  return {
    ...merged,
    requires_you: merged.requires_you.filter((item) => !coveredByMoney(item, units)),
    in_progress: merged.in_progress.filter((item) => !coveredByMoney(item, units)),
    waiting: merged.waiting.filter((item) => !coveredByMoney(item, units)),
  };
}

export type AttentionSliceName = 'control_plane' | 'money' | 'booking_risk';

const SLICE_SOURCES: Record<AttentionSliceName, Array<keyof AttentionReadModel['sources']>> = {
  control_plane: ['jack_inbox', 'guest_inbox', 'control_plane_runs'],
  money: ['money'],
  booking_risk: ['oleg'],
};

export function failedAttentionSlice(name: AttentionSliceName, status: AttentionSourceStatus = 'unavailable'): AttentionSlice {
  const sources: AttentionSlice['sources'] = {};
  for (const key of SLICE_SOURCES[name]) sources[key] = { status, count: 0 };
  return { requires_you: [], in_progress: [], waiting: [], sources };
}

/** A source that failed after it was configured. Optional integrations that were never switched on are not failures. */
export function attentionSourceFailed(key: string, status: AttentionSourceStatus | undefined): boolean {
  if (!status) return false;
  if (key === 'inbox_triage_done') return false;
  return status !== 'ok' && status !== 'pending' && status !== 'not_configured';
}

/** Unconfigured integrations limit coverage without constituting a failed request. */
export function attentionSourceUnchecked(key: string, status: AttentionSourceStatus | undefined): boolean {
  return key !== 'inbox_triage_done' && status === 'not_configured';
}

/** Errors, pending reads and unconfigured coverage are distinct states. */
export function attentionDayCoverage(
  model: AttentionReadModel | null,
  failed: boolean,
): 'loading' | 'unavailable' | 'partial' | 'limited' | 'complete' {
  if (failed) return 'unavailable';
  if (!model) return 'loading';
  if (!model.sources.decisions || !model.sources.task_inbox) return 'partial';
  const blocks = Object.entries(model.sources).some(([key, source]) => attentionSourceFailed(key, source?.status));
  if (blocks) return 'partial';
  if (Object.values(model.sources).some(source => source?.status === 'pending')) return 'loading';
  const unchecked = Object.entries(model.sources).some(([key, source]) => attentionSourceUnchecked(key, source?.status));
  return unchecked ? 'limited' : 'complete';
}
